/**
 * StatusBar — minimal, always-visible session footer.
 *
 * Composition (left → right):
 *   <conversation id> · <tools> · <files modified> · <elapsed>
 *   ⌬ · ∩ · ∞                                          <cwd>
 *
 * The family sigil is rendered in the muted accent so it acts as a
 * subtle watermark rather than competing with content.
 */

import React, { useMemo } from 'react';
import { Box, Text } from 'ink';
import { useTerminalSize } from '../hooks/index.js';
import { COLORS, FAMILY_SIGIL, SYMBOLS } from '../theme/ink-theme.js';
import type { SessionStats } from '../hooks/useCommands.js';

interface StatusBarProps {
    conversationId?: string;
    stats: SessionStats;
}

function formatElapsed(ms: number): string {
    if (ms < 60_000) return `${Math.max(0, Math.floor(ms / 1000))}s`;
    const mins = Math.floor(ms / 60_000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins > 0 ? `${hrs}h${remMins}m` : `${hrs}h`;
}

function getDisplayCwd(): string {
    const home = process.env['HOME'];
    const cwd = process.cwd();
    if (home && home.length > 0 && cwd.startsWith(home)) {
        return '~' + cwd.slice(home.length);
    }
    return cwd;
}

function truncateLeft(s: string, max: number): string {
    if (s.length <= max) return s;
    if (max <= 1) return '…';
    return '…' + s.slice(-(max - 1));
}

export const StatusBar: React.FC<StatusBarProps> = ({
    conversationId,
    stats,
}) => {
    const { columns } = useTerminalSize();
    const safeCols = Math.max(20, columns || 80);

    const leftParts = useMemo(() => {
        const parts: string[] = [];

        if (conversationId) {
            parts.push(`${SYMBOLS.pin} ${conversationId.substring(0, 8)}`);
        }

        if (stats.toolCallCount > 0) {
            parts.push(
                `${stats.toolCallCount} ${stats.toolCallCount === 1 ? 'tool' : 'tools'}`,
            );
        }

        if (stats.filesModified.size > 0) {
            const n = stats.filesModified.size;
            parts.push(`${n} ${n === 1 ? 'file' : 'files'} modified`);
        }

        const elapsed = Date.now() - stats.startedAt;
        if (elapsed >= 1000) {
            parts.push(formatElapsed(elapsed));
        }

        return parts.join(`  ${SYMBOLS.bulletSm}  `);
    }, [conversationId, stats.toolCallCount, stats.filesModified.size, stats.startedAt]);

    const cwdRaw = useMemo(() => getDisplayCwd(), []);

    // Budget the available width so we never wrap.
    // Layout: " <left>   <sigil>   <cwd> "  with 2-char gutters either side.
    const sigilWidth = FAMILY_SIGIL.length;
    const fixedOverhead = 2 /* edges */ + 3 /* gap before sigil */ + 3 /* gap after sigil */;
    const remaining = Math.max(8, safeCols - leftParts.length - sigilWidth - fixedOverhead);
    const cwd = truncateLeft(cwdRaw, remaining);

    const spacerLen = Math.max(
        2,
        safeCols - leftParts.length - sigilWidth - cwd.length - 2,
    );

    return (
        <Box marginTop={0}>
            <Text dimColor>{leftParts}</Text>
            <Text>{' '.repeat(Math.max(2, Math.floor(spacerLen / 2)))}</Text>
            <Text color={COLORS.muted}>{FAMILY_SIGIL}</Text>
            <Text>{' '.repeat(Math.max(2, Math.ceil(spacerLen / 2)))}</Text>
            <Text dimColor>{cwd}</Text>
        </Box>
    );
};
